import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar";
import PageTitle from "../../components/PageTitle";
import { allFirstDiv } from "../../components/DesignStandardize";

const LoginLogs = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const [attempts, setAttempts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 50,
    totalPages: 1,
  });
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/admin/login-attempts/all`, {
        params: {
          ...(email && { email }),
          ...(userType && { userType }),
          page,
        },
      });
      setAttempts(response.data.attempts);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.message || err.response.data?.error || "Failed to load login logs");
      } else if (err.request) {
        setError("Network error: could not reach the server. Please check your connection and try again.");
      } else {
        setError("Something went wrong while loading login logs.");
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, email, userType, page]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAttempts();
  };

  return (
    <>
      <Navbar />
      <PageTitle title="Login Logs" />
      <div className={`${allFirstDiv} min-h-screen bg-[#F5F5F5] py-12 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto space-y-8">
          <h1 className="text-4xl font-extrabold text-[#2D2D2D] text-center mt-5">Login Logs</h1>

          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm">
            <div className="p-6 sm:p-8 space-y-6">
              <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Search by email..."
                    className="block w-64 px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    className="block w-40 px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  >
                    <option value="">All</option>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition duration-200 shadow-md"
                >
                  Filter
                </button>
              </form>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : attempts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="mt-2 text-lg">No login attempts found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Time</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Result</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Reason</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User Agent</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {attempts.map((attempt) => (
                          <tr key={attempt.id} className="hover:bg-blue-50 transition duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {new Date(attempt.createdAt).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                              {attempt.userType}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{attempt.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  attempt.success
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {attempt.success ? "Success" : "Failed"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{attempt.reason}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{attempt.ipAddress}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={attempt.userAgent}>
                              {attempt.userAgent}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center pt-4">
                    <span className="text-sm text-gray-500">
                      Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        disabled={pagination.page <= 1}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                        disabled={pagination.page >= pagination.totalPages}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginLogs;
